import { ApiError } from './client'

// Write-failure classification — ATC-129.
//
// THE QUESTION THIS ANSWERS IS "DID IT TAKE EFFECT?", not "what went wrong".
// It exists for the wizard (ATC-133), where a sequence of non-transactional
// writes has to tell an operator what actually happened — but every write
// surface benefits, because the answer decides whether a retry button is safe
// to offer at all.

export type WriteOutcome = 'not-applied' | 'unknown'

export interface WriteFailure {
  outcome: WriteOutcome
  /** The server's `error.code` when there was one. */
  code?: string
  message: string
}

/**
 * `4xx` → **not-applied**. Every ATC handler validates and refuses BEFORE any
 * store call — the closed-schema guard, the ops-group check, the committed
 * floor, the class rules. A 4xx is therefore a promise that nothing was
 * written, and a retry after fixing the input is safe.
 *
 * `5xx` or a thrown error → **unknown**, and the distinction is the whole
 * point. `500` means ATC could not classify its own outcome, so a row may or
 * may not exist. A thrown `TypeError`/network failure is worse: the request may
 * have been received and answered while the response was lost.
 *
 * WHAT THE CALLER MUST DO WITH `unknown`: not offer a one-click retry. On an
 * append-only ledger a retry double-grants; on issuance it mints an orphan
 * environment and a token nobody holds. The recovery is to go and look.
 */
export function describeWriteFailure(err: unknown): WriteFailure {
  if (err instanceof ApiError) {
    const outcome: WriteOutcome = err.status >= 400 && err.status < 500 ? 'not-applied' : 'unknown'
    return { outcome, code: err.code, message: err.message }
  }
  return {
    outcome: 'unknown',
    message: err instanceof Error ? err.message : String(err),
  }
}

/**
 * Whether a one-click retry is safe to offer.
 *
 * Deliberately NOT the same question as "was this a 4xx". A `not-applied`
 * failure is safe to retry in the sense that nothing was written — but a retry
 * of the identical request would fail identically, so the caller should be
 * fixing the input, not resubmitting. This returns true only when a retry could
 * plausibly succeed AND cannot duplicate.
 */
export function isRetrySafe(failure: WriteFailure): boolean {
  return failure.outcome === 'not-applied'
}
